import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { registerUser } from "../../../data";
import AuthStore from "../../../stores/AuthStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";

export function RegisterScreen() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { control, handleSubmit, formState: { errors } } = useForm();

  const { mutate: registerMutation, isLoading } = useMutation({
    mutationKey: "register",
    mutationFn: (data) => registerUser(data),
    onSuccess: (response) => {
      AuthStore.setUser(response.data.token);
      navigate("/dashboard");
      toast({
        title: t("auth.registerSuccess"),
        description: t("auth.welcomeBack"),
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("auth.registerFailed"),
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data) => {
    registerMutation(data);
  };

  return (
    <AuthLayout 
      title={t("auth.signUpTitle")} 
      description={t("auth.signUpSubtitle")}
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t("auth.fullName")}</Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("errors.required") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="name"
                    placeholder={t("profile.namePlaceholder")}
                    type="text"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Controller
                name="email"
                control={control}
                rules={{ required: t("errors.required") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    placeholder={t("profile.emailPlaceholder")}
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: t("errors.required") }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      disabled={isLoading}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.signUpWithEmail")}
            </Button>
          </div>
        </form>
        
        <p className="px-8 text-center text-sm text-muted-foreground">
          {t("auth.alreadyHaveAccount")}{" "}
          <Link
            to="/login"
            className="underline underline-offset-4 hover:text-primary"
          >
            {t("auth.signIn")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
